import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useLineOfficialAccounts, useResolve } from '../hooks/useResolver.js';
import type { ResolveSummary } from '../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack line-user-id-resolver
 * @graph-domain Marketing, Operations
 * @graph-business arthur_user_id → line_user_id reveal ページ。CSV アップロード、対象 LINE 公式アカウント選択、reveal ペア表示。アクセス制御は __root で canAccess（admin か app_access 経由）に集約。matchedPairs はメモリ上にだけ保持し、永続化しない
 * @graph-connects useLineOfficialAccounts [calls] LINE 公式アカウント一覧
 * @graph-connects useResolve [calls] resolve mutation
 */
export const Route = createFileRoute('/')({
  component: ResolvePage,
});

/** @graph-connects none */
function downloadBlobCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

/** @graph-connects none */
function downloadMatchedPairsCsv(summary: ResolveSummary): void {
  const lines = ['arthur_user_id,line_user_id'];
  for (const pair of summary.matchedPairs) {
    lines.push(`${String(pair.arthurUserId)},${pair.lineUserId}`);
  }
  downloadBlobCsv(`line-user-id-reveal-${summary.jobId}.csv`, lines.join('\n'));
}

/** @graph-connects none */
function downloadUnmatchedFriendsCsv(summary: ResolveSummary): void {
  const lines = ['line_user_id', ...summary.unmatchedFriends];
  downloadBlobCsv(`line-user-id-unmatched-${summary.jobId}.csv`, lines.join('\n'));
}

/** @graph-connects none */
async function copyMatchedPairs(summary: ResolveSummary): Promise<void> {
  const text = summary.matchedPairs
    .map((pair) => `${String(pair.arthurUserId)}\t${pair.lineUserId}`)
    .join('\n');
  await navigator.clipboard.writeText(text);
}

/** @graph-connects none */
async function copyUnmatchedFriends(summary: ResolveSummary): Promise<void> {
  await navigator.clipboard.writeText(summary.unmatchedFriends.join('\n'));
}

/** @graph-connects none */
function ResolvePage() {
  const { t } = useTranslation();

  const accountsQuery = useLineOfficialAccounts();
  const resolveMutation = useResolve();

  const [accountId, setAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [csv, setCsv] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ResolveSummary | null>(null);

  const accounts = useMemo(() => accountsQuery.data?.accounts ?? [], [accountsQuery.data]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setCopyError(null);
    setSummary(null);
    try {
      const result = await resolveMutation.mutateAsync({
        lineOfficialAccountId: accountId,
        reason,
        csv,
      });
      setSummary(result);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold">{t('resolve.title')}</h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('resolve.description')}
        </p>
      </header>

      <section className="glass-card space-y-4 p-4">
        <form className="space-y-3" onSubmit={(e) => void handleSubmit(e)}>
          <div>
            <label className="block text-sm font-medium" htmlFor="line-account">
              {t('resolve.account')}
            </label>
            <select
              id="line-account"
              className="mt-1 w-full rounded border px-2 py-1"
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
              }}
              required
            >
              <option value="">{t('resolve.selectAccount')}</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium" htmlFor="reason">
              {t('resolve.reason')}
            </label>
            <input
              id="reason"
              type="text"
              className="mt-1 w-full rounded border px-2 py-1"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              maxLength={500}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium" htmlFor="csv">
              {t('resolve.csv')}
              <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                ({t('resolve.csvHint')})
              </span>
            </label>
            <textarea
              id="csv"
              className="mt-1 w-full rounded border px-2 py-1 font-mono text-xs"
              rows={8}
              value={csv}
              onChange={(e) => {
                setCsv(e.target.value);
              }}
              placeholder={t('resolve.csvPlaceholder')}
              required
            />
          </div>

          {submitError ? <p className="text-sm text-[var(--color-error)]">{submitError}</p> : null}

          <button
            type="submit"
            className="rounded bg-[var(--color-accent-primary)] px-4 py-2 text-white"
            disabled={resolveMutation.isPending}
          >
            {resolveMutation.isPending ? t('resolve.submitting') : t('resolve.submit')}
          </button>
        </form>
      </section>

      {summary ? (
        <section className="glass-card space-y-3 p-4">
          <h2 className="text-lg font-semibold">{t('resolve.result.title')}</h2>
          <dl className="grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-2">
            <dt className="text-[var(--color-text-secondary)]">
              {t('resolve.result.csvUserCount')}
            </dt>
            <dd>{summary.csvUserCount}</dd>
            <dt className="text-[var(--color-text-secondary)]">
              {t('resolve.result.friendCount')}
            </dt>
            <dd>{summary.friendCount}</dd>
            <dt className="text-[var(--color-text-secondary)]">
              {t('resolve.result.matchedCount')}
            </dt>
            <dd className="font-bold">{summary.matchedCount}</dd>
            <dt className="text-[var(--color-text-secondary)]">
              {t('resolve.result.unmatchedFriendCount')}
            </dt>
            <dd>{summary.unmatchedFriendCount}</dd>
            <dt className="text-[var(--color-text-secondary)]">{t('resolve.result.executedAt')}</dt>
            <dd className="font-mono text-xs">{summary.executedAt}</dd>
            {summary.errorSummary ? (
              <>
                <dt className="text-[var(--color-error)]">{t('resolve.result.errorSummary')}</dt>
                <dd className="text-[var(--color-error)]">{summary.errorSummary}</dd>
              </>
            ) : null}
          </dl>

          {summary.matchedPairs.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('resolve.result.matchedPairsTitle')}</h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm"
                  onClick={() => {
                    setCopyError(null);
                    void copyMatchedPairs(summary)
                      .then(() => {
                        setCopyError(null);
                      })
                      .catch((err: unknown) => {
                        setCopyError(
                          err instanceof Error ? err.message : t('resolve.result.copyFailed'),
                        );
                      });
                  }}
                >
                  {t('resolve.result.copy')}
                </button>
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm"
                  onClick={() => {
                    downloadMatchedPairsCsv(summary);
                  }}
                >
                  {t('resolve.result.downloadCsv')}
                </button>
              </div>
              <div className="max-h-80 overflow-auto rounded border">
                <table className="w-full text-left text-xs font-mono">
                  <thead className="bg-[var(--color-surface-secondary)]">
                    <tr>
                      <th className="px-2 py-1">arthur_user_id</th>
                      <th className="px-2 py-1">line_user_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.matchedPairs.map((pair) => (
                      <tr key={pair.arthurUserId}>
                        <td className="px-2 py-1">{pair.arthurUserId}</td>
                        <td className="px-2 py-1">{pair.lineUserId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {summary.unmatchedFriends.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('resolve.result.unmatchedFriendsTitle')}</h3>
              <p className="text-xs text-[var(--color-text-secondary)]">
                {t('resolve.result.unmatchedFriendsDescription')}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm"
                  onClick={() => {
                    setCopyError(null);
                    void copyUnmatchedFriends(summary)
                      .then(() => {
                        setCopyError(null);
                      })
                      .catch((err: unknown) => {
                        setCopyError(
                          err instanceof Error ? err.message : t('resolve.result.copyFailed'),
                        );
                      });
                  }}
                >
                  {t('resolve.result.copyUnmatched')}
                </button>
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm"
                  onClick={() => {
                    downloadUnmatchedFriendsCsv(summary);
                  }}
                >
                  {t('resolve.result.downloadUnmatchedCsv')}
                </button>
              </div>
              <div className="max-h-80 overflow-auto rounded border">
                <table className="w-full text-left text-xs font-mono">
                  <thead className="bg-[var(--color-surface-secondary)]">
                    <tr>
                      <th className="px-2 py-1">line_user_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.unmatchedFriends.map((lineUserId) => (
                      <tr key={lineUserId}>
                        <td className="px-2 py-1">{lineUserId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {copyError ? <p className="text-sm text-[var(--color-error)]">{copyError}</p> : null}
        </section>
      ) : null}
    </div>
  );
}
